import { Head, Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import type { ReactNode } from 'react'

import { PageHeader } from '@/components/PageHeader'
import { Button } from '@/components/ui/button'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { fundPath } from '@/lib/routes'

import { FundFormFields, type FundFormData } from './_Form'

type EditFund = FundFormData & { id: number }

type FundsEditProps = {
  fund: EditFund
}

function FundsEdit({ fund }: FundsEditProps) {
  const form = useForm<FundFormData>({
    name: fund.name ?? '',
    slug: fund.slug ?? '',
    vintage_year: fund.vintage_year,
  })

  return (
    <>
      <Head title={`${fund.name} — Funds`} />

      <div className="mx-auto max-w-3xl px-8 py-10">
        <Link
          href={fundPath(fund.slug)}
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to fund
        </Link>

        <PageHeader title={fund.name} description="Edit fund details." />

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.transform((data) => ({ fund: data }))
            form.patch(fundPath(fund.slug))
          }}
        >
          <FundFormFields
            data={form.data}
            errors={form.errors as Partial<Record<keyof FundFormData, string>>}
            setData={form.setData}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Link href={fundPath(fund.slug)}>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </Link>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </>
  )
}

export default Object.assign(FundsEdit, {
  layout: (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>,
})
